import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowRight, 
  Mail, 
  Users, 
  Target, 
  Check, 
  Star, 
  TrendingUp, 
  Shield,
  CreditCard,
  Clock,
  Edit3,
  Sparkles,
  Play,
  RefreshCw,
  AlertCircle,
  Bot,
  Zap,
  Brain,
  Server,
  DollarSign
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface FlowState {
  step: number;
  businessType: string;
  email: string;
  selectedPlan: 'autonomous_ai' | 'usage' | null;
}

interface LiveDemo {
  businessType: string;
  productExample: string;
  automationSteps: Array<{
    title: string;
    description: string;
    icon: React.ReactNode;
  }>;
  results: {
    setup: string;
    emails: string;
    meetings: string;
    cost: string;
  };
  costSavings: string;
}

// Real examples showing the AI automation process
const liveDemoExamples: LiveDemo[] = [
  {
    businessType: "SaaS Companies",
    productExample: "AI-powered project management tool for remote teams",
    automationSteps: [
      {
        title: "AI Analyzes Your Business",
        description: "Understands your product, target audience, and value proposition",
        icon: <Brain className="w-5 h-5" />
      },
      {
        title: "AI Sets Up Infrastructure", 
        description: "Auto-configures servers, domains, and tracking systems",
        icon: <Server className="w-5 h-5" />
      },
      {
        title: "AI Finds Perfect Prospects",
        description: "Searches 250M+ database for ideal customers actively looking for solutions",
        icon: <Users className="w-5 h-5" />
      },
      {
        title: "AI Writes & Sends Emails",
        description: "Creates personalized emails, sends automatically, responds to replies",
        icon: <Mail className="w-5 h-5" />
      },
      {
        title: "AI Self-Optimizes",
        description: "Tracks conversions, improves copy and targeting automatically",
        icon: <Zap className="w-5 h-5" />
      }
    ],
    results: {
      setup: "2 minutes",
      emails: "847 sent automatically",
      meetings: "23 booked this month",
      cost: "$99/month"
    },
    costSavings: "vs $4,200/month (SDR + tools + infrastructure)"
  },
  {
    businessType: "Marketing Agencies",
    productExample: "Full-service digital marketing for local businesses",
    automationSteps: [
      {
        title: "AI Learns Your Services",
        description: "Maps your agency offerings, case studies, and client results",
        icon: <Brain className="w-5 h-5" />
      },
      {
        title: "AI Builds Campaign System",
        description: "Creates dedicated infrastructure for high-volume outreach",
        icon: <Server className="w-5 h-5" />
      },
      {
        title: "AI Targets Business Owners",
        description: "Identifies local businesses struggling with digital marketing",
        icon: <Users className="w-5 h-5" />
      },
      {
        title: "AI Crafts Agency Outreach",
        description: "Writes compelling case study emails, handles prospect conversations",
        icon: <Mail className="w-5 h-5" />
      },
      {
        title: "AI Optimizes Performance",
        description: "A/B tests messaging, improves targeting based on response rates",
        icon: <Zap className="w-5 h-5" />
      }
    ],
    results: {
      setup: "90 seconds",
      emails: "1,240 sent on autopilot",
      meetings: "31 discovery calls",
      cost: "$99/month"
    },
    costSavings: "vs $6,800/month (business development team + tools)"
  },
  {
    businessType: "Real Estate Teams",
    productExample: "Luxury home sales in Austin market",
    automationSteps: [
      {
        title: "AI Studies Your Market",
        description: "Analyzes your listings, target demographics, and local market data",
        icon: <Brain className="w-5 h-5" />
      },
      {
        title: "AI Deploys Sales System",
        description: "Sets up automated prospecting and lead nurturing infrastructure",
        icon: <Server className="w-5 h-5" />
      },
      {
        title: "AI Finds Ready Buyers/Sellers",
        description: "Identifies homeowners with buying/selling triggers (job changes, life events)",
        icon: <Users className="w-5 h-5" />
      },
      {
        title: "AI Handles Outreach",
        description: "Sends personalized market updates, responds to inquiries automatically",
        icon: <Mail className="w-5 h-5" />
      },
      {
        title: "AI Improves Conversions",
        description: "Optimizes messaging based on appointment bookings and showings",
        icon: <Zap className="w-5 h-5" />
      }
    ],
    results: {
      setup: "3 minutes",
      emails: "2,100 sent automatically",
      meetings: "18 qualified appointments", 
      cost: "$99/month"
    },
    costSavings: "vs $3,500/month (lead generation services + CRM + assistants)"
  }
];

const ImprovedFlow: React.FC = () => {
  const [flowState, setFlowState] = useState<FlowState>({
    step: 1,
    businessType: '',
    email: '',
    selectedPlan: null
  });
  
  const [currentDemo, setCurrentDemo] = useState<LiveDemo>(liveDemoExamples[0]);
  const [currentDemoIndex, setCurrentDemoIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [shakeEmail, setShakeEmail] = useState(false);
  
  // Refs for auto-focus optimization
  const emailInputRef = useRef<HTMLInputElement>(null);
  const businessInputRef = useRef<HTMLInputElement>(null);
  
  const { toast } = useToast();

  // Cycle through live examples every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDemoIndex((prev) => (prev + 1) % liveDemoExamples.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Update current demo when index changes
  useEffect(() => {
    setCurrentDemo(liveDemoExamples[currentDemoIndex]);
  }, [currentDemoIndex]);

  // Removed auto-focus that was causing cursor jumping issues
  // Users can navigate to email field naturally when ready

  // Email validation with shake animation
  const validateEmail = (email: string) => {
    if (!email.trim()) {
      setEmailError('Email required to create your autonomous AI marketer');
      setShakeEmail(true);
      setTimeout(() => setShakeEmail(false), 500);
      return false;
    }
    if (!email.includes('@') || !email.includes('.')) {
      setEmailError('Please enter a valid email address');
      setShakeEmail(true);
      setTimeout(() => setShakeEmail(false), 500);
      return false;
    }
    setEmailError('');
    return true;
  };

  // Improved demo tag click handler with pre-fill (no auto-focus)
  const handleDemoTagClick = (businessType: string) => {
    setFlowState(prev => ({ ...prev, businessType }));
    // Remove auto-focus to prevent unwanted scrolling
    // The user can click on the input if they want to edit
  };

  const handleStepOne = async () => {
    if (!flowState.businessType.trim()) {
      if (businessInputRef.current) {
        businessInputRef.current.focus();
      }
      return;
    }
    
    if (!validateEmail(flowState.email)) {
      return;
    }
    
    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsProcessing(false);
    setFlowState(prev => ({ ...prev, step: 2 }));
  };

  const handleLaunch = (plan: 'autonomous_ai' | 'usage') => {
    const params = new URLSearchParams({
      businessType: flowState.businessType,
      email: flowState.email,
      plan: plan,
      price: (plan === 'autonomous_ai' ? 97 : 0).toString(),
      source: 'autonomous-ai-flow'
    });
    
    localStorage.setItem('munger_ai_setup', JSON.stringify(flowState));
    window.location.href = `https://auth.munger.ai/#/choose-plan?${params.toString()}`;
  };

  return (
    <section className="min-h-screen flex flex-col justify-center items-center text-center px-4 sm:px-6 py-20 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-slate-900 to-gray-950" />
      <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_20%_30%,_rgba(59,130,246,0.4)_0%,_transparent_50%),radial-gradient(circle_at_80%_70%,_rgba(147,51,234,0.3)_0%,_transparent_50%)]" />
      
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Step Progress Indicator - Now 2 steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2">
            {[1, 2].map((stepNum) => (
              <div
                key={stepNum}
                className={`transition-all duration-500 rounded-full ${
                  stepNum <= flowState.step 
                    ? 'w-8 h-3 bg-gradient-to-r from-purple-500 to-blue-500' 
                    : 'w-3 h-3 bg-gray-600'
                }`}
              />
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Hero + Autonomous AI Demo */}
          {flowState.step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-green-500/20 via-blue-500/15 to-purple-500/20 backdrop-blur-sm border border-green-500/40 mb-8 shadow-xl">
                <Bot className="w-4 h-4 text-green-400" />
                <span className="text-sm font-semibold bg-gradient-to-r from-green-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                  World's First Autonomous AI Marketer
                </span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight">
                <span className="block text-white">Create a "Product" →</span>
                <span className="block mt-2 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  AI Does Everything Else
                </span>
              </h1>
              
              <p className="text-lg md:text-xl text-gray-300 mb-4 max-w-3xl mx-auto">
                The first AI system that sets up servers, writes personalized emails, finds prospects, 
                responds to replies, and self-optimizes completely on autopilot.
              </p>
              
              <div className="flex items-center justify-center gap-8 mb-8 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <span>90% of work done by AI</span>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-400" />
                  <span>Cuts $1000s in costs</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-400" />
                  <span>2 min setup</span>
                </div>
              </div>

              {/* Live Autonomous AI Demo */}
              <div className="relative">
                {/* Demo Switcher */}
                <div className="flex items-center justify-center gap-4 mb-6">
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-800/50 border border-gray-700/50">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    <span className="text-sm text-gray-300">Live Demo:</span>
                    <span className="text-sm font-semibold text-white">{currentDemo.businessType}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentDemoIndex((prev) => (prev + 1) % liveDemoExamples.length)}
                    className="text-purple-400 border-purple-500/30 hover:bg-purple-500/10"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Next Business Type
                  </Button>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentDemoIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5 }}
                    className="space-y-6"
                  >
                    {/* Product Example */}
                    <div className="bg-gradient-to-r from-blue-500/15 to-purple-500/15 rounded-2xl p-6 border border-blue-500/30 max-w-3xl mx-auto">
                      <h3 className="text-lg font-semibold text-white mb-3">Step 1: You Create a "Product" (2 minutes)</h3>
                      <div className="bg-gray-900/50 rounded-lg p-4 text-left">
                        <div className="text-blue-300 text-sm font-medium">Business Type: {currentDemo.businessType}</div>
                        <div className="text-gray-300 text-sm mt-2">{currentDemo.productExample}</div>
                        <div className="text-green-400 text-xs mt-3">✓ Business context captured by AI</div>
                      </div>
                    </div>

                    {/* AI Automation Steps */}
                    <div className="bg-gradient-to-r from-purple-500/15 to-pink-500/15 rounded-2xl p-6 border border-purple-500/30 max-w-4xl mx-auto">
                      <h3 className="text-lg font-semibold text-white mb-4">Step 2: AI Handles Everything Automatically</h3>
                      <div className="grid md:grid-cols-5 gap-4">
                        {currentDemo.automationSteps.map((step, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: index * 0.1 }}
                            className="bg-gray-900/30 rounded-lg p-3 text-left"
                          >
                            <div className="text-purple-400 mb-2">{step.icon}</div>
                            <div className="text-white text-xs font-medium mb-1">{step.title}</div>
                            <div className="text-gray-400 text-xs">{step.description}</div>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    {/* Results & Cost Savings */}
                    <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                      <div className="bg-gradient-to-r from-green-500/15 to-emerald-500/15 rounded-2xl p-6 border border-green-500/30">
                        <h3 className="text-lg font-semibold text-white mb-4">Autopilot Results</h3>
                        <div className="space-y-3 text-left">
                          <div className="flex justify-between">
                            <span className="text-gray-300 text-sm">Setup Time:</span>
                            <span className="text-green-400 text-sm font-medium">{currentDemo.results.setup}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300 text-sm">Emails Sent:</span>
                            <span className="text-blue-400 text-sm font-medium">{currentDemo.results.emails}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300 text-sm">Meetings/Calls:</span>
                            <span className="text-purple-400 text-sm font-medium">{currentDemo.results.meetings}</span>
                          </div>
                          <div className="flex justify-between border-t border-gray-700 pt-3">
                            <span className="text-gray-300 text-sm">Monthly Cost:</span>
                            <span className="text-white text-sm font-bold">{currentDemo.results.cost}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-yellow-500/15 to-orange-500/15 rounded-2xl p-6 border border-yellow-500/30">
                        <h3 className="text-lg font-semibold text-white mb-4">Traditional Approach Cost</h3>
                        <div className="text-center">
                          <div className="text-3xl font-bold text-red-400 mb-2">{currentDemo.costSavings.split('vs ')[1].split('/')[0]}/month</div>
                          <div className="text-gray-300 text-sm mb-4">{currentDemo.costSavings.split('(')[1].replace(')', '')}</div>
                          <div className="bg-gray-900/50 rounded-lg p-3">
                            <div className="text-green-400 text-lg font-bold">
                              Save {Math.round((parseInt(currentDemo.costSavings.match(/\$([0-9,]+)/)?.[1]?.replace(',', '') || '0') - 99) / 99 * 100)}%
                            </div>
                            <div className="text-gray-400 text-xs">with AI automation</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Main CTA Form */}
              <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 rounded-2xl p-8 border border-gray-700/50 max-w-2xl mx-auto">
                <h3 className="text-2xl font-semibold text-white mb-4">
                  Ready for Your Autonomous AI Marketer?
                </h3>
                <p className="text-gray-300 mb-6">
                  Tell us your business type and we'll create your AI system in 2 minutes
                </p>
                
                <div className="space-y-4">
                  {/* Business Type Input */}
                  <div>
                    <Input
                      ref={businessInputRef}
                      value={flowState.businessType}
                      onChange={(e) => setFlowState(prev => ({ ...prev, businessType: e.target.value }))}
                      placeholder="e.g., SaaS company, Marketing agency, Real estate team..."
                      className="h-12 text-base bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-purple-500"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleStepOne();
                        }
                      }}
                      disabled={isProcessing}
                    />
                    <div className="mt-2 text-center">
                      <details className="text-gray-400">
                        <summary className="text-xs cursor-pointer hover:text-purple-400 transition-colors">
                          Or pick from examples above
                        </summary>
                        <div className="mt-2 flex flex-wrap gap-2 justify-center">
                          {liveDemoExamples.map((demo) => (
                            <button
                              key={demo.businessType}
                              onClick={() => handleDemoTagClick(demo.businessType)}
                              className="text-xs px-3 py-1 bg-gray-700/50 hover:bg-purple-600/30 rounded-full transition-colors"
                            >
                              {demo.businessType}
                            </button>
                          ))}
                        </div>
                      </details>
                    </div>
                  </div>

                  {/* Email Input with enhanced validation */}
                  <div>
                    <div className="relative">
                      <Input
                        ref={emailInputRef}
                        value={flowState.email}
                        onChange={(e) => {
                          setFlowState(prev => ({ ...prev, email: e.target.value }));
                          if (emailError) {
                            setEmailError('');
                          }
                        }}
                        placeholder="your.email@company.com (required)"
                        type="email"
                        className={`h-12 text-base bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-yellow-500 transition-all duration-300 ${
                          shakeEmail ? 'animate-pulse border-red-500' : ''
                        } ${emailError ? 'border-red-500' : ''}`}
                        disabled={isProcessing}
                        required
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleStepOne();
                          }
                        }}
                      />
                      {emailError && (
                        <div className="absolute -bottom-6 left-0 flex items-center gap-1 text-red-400 text-xs">
                          <AlertCircle className="w-3 h-3" />
                          {emailError}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      We'll create your autonomous AI marketer and send you the setup details
                    </p>
                  </div>

                  {/* CTA Button */}
                  <Button
                    onClick={handleStepOne}
                    disabled={!flowState.businessType.trim() || !flowState.email.trim() || isProcessing}
                    className="w-full h-14 px-6 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold text-lg mt-8"
                  >
                    {isProcessing ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Creating Your AI Marketer...
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Bot className="w-5 h-5" />
                        Create My AI Marketer
                        <ArrowRight className="w-5 h-5" />
                      </div>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Choose Plan & Launch */}
          {flowState.step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-green-500/15 via-emerald-500/10 to-teal-500/15 backdrop-blur-sm border border-green-500/30 mb-8 shadow-xl">
                <CreditCard className="w-4 h-4 text-green-400" />
                <span className="text-sm font-semibold bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 bg-clip-text text-transparent">
                  Choose Your AI System & Launch
                </span>
              </div>

              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                    Perfect! Your AI System is Ready
                  </span>
                </h2>
                
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 mb-4">
                  <Bot className="w-4 h-4 text-purple-400" />
                  <span className="text-sm text-purple-300">
                    Autonomous AI configured for: <strong>{flowState.businessType}</strong>
                  </span>
                </div>

                {/* Quick Preview of Their AI System */}
                <div className="bg-gradient-to-r from-gray-800/30 to-gray-900/30 rounded-xl p-6 border border-gray-700/50 max-w-3xl mx-auto mb-8">
                  <h3 className="text-lg font-semibold text-white mb-4">Your AI Marketer Will Handle:</h3>
                  <div className="grid md:grid-cols-3 gap-4 text-left">
                    <div className="bg-gray-900/50 rounded-lg p-4">
                      <div className="text-blue-400 mb-2"><Server className="w-5 h-5" /></div>
                      <div className="text-white text-sm font-medium">Infrastructure Setup</div>
                      <div className="text-gray-400 text-xs">Servers, domains, tracking</div>
                    </div>
                    <div className="bg-gray-900/50 rounded-lg p-4">
                      <div className="text-green-400 mb-2"><Users className="w-5 h-5" /></div>
                      <div className="text-white text-sm font-medium">Prospect Finding</div>
                      <div className="text-gray-400 text-xs">250M+ database search</div>
                    </div>
                    <div className="bg-gray-900/50 rounded-lg p-4">
                      <div className="text-purple-400 mb-2"><Brain className="w-5 h-5" /></div>
                      <div className="text-white text-sm font-medium">Email Writing & Optimization</div>
                      <div className="text-gray-400 text-xs">Automatic A/B testing</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing Cards - Simplified */}
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {/* Growth Plan - Highlighted */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative bg-gradient-to-br from-purple-500/10 via-blue-500/10 to-pink-500/10 rounded-2xl p-8 border-2 border-purple-500/50 shadow-xl"
                >
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      MOST POPULAR
                    </div>
                  </div>
                  
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">Autonomous AI Marketer</h3>
                    <div className="text-4xl font-bold text-white mb-2">
                      $97
                      <span className="text-lg text-gray-400 font-normal">/month</span>
                    </div>
                    <div className="bg-gradient-to-r from-green-500/15 to-emerald-500/15 rounded-lg p-3 border border-green-500/30 mb-2">
                      <div className="text-green-400 text-sm font-semibold">
                        Replaces $4,200/month in costs:
                      </div>
                      <div className="text-xs text-gray-300 mt-1">
                        SDR ($3,500) + Tools ($400) + Infrastructure ($300)
                      </div>
                    </div>
                    <div className="text-green-400 text-xs font-medium">
                      97% cost savings • Full ROI after first deal
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    {[
                      '50K emails/month included',
                      'AI configures servers & domains',
                      'Finds prospects from 250M+ database',
                      'Writes & sends personalized emails',
                      'Responds to replies automatically', 
                      'Self-optimizes campaigns 24/7',
                      'Tracks conversions with pixels'
                    ].map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="bg-gray-800/30 rounded-lg p-4 mb-6">
                    <div className="text-center">
                      <div className="text-white text-sm font-semibold mb-1">Pricing beyond 50K emails:</div>
                      <div className="text-green-400 text-sm">$8 per 10K additional emails</div>
                      <div className="text-gray-400 text-xs mt-1">Scales as you grow, pay only for what you use</div>
                    </div>
                  </div>

                  {/* Enhanced micro-testimonial with avatar */}
                  <div className="mb-4 p-4 bg-gray-800/30 rounded-lg border border-green-500/20">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full flex items-center justify-center text-white font-bold text-sm">
                        JM
                      </div>
                      <div>
                        <div className="text-green-400 text-sm font-semibold">"$47K revenue in first month"</div>
                        <div className="text-gray-400 text-xs">Jake Mitchell, SaaS Founder</div>
                      </div>
                    </div>
                    {/* Trustpilot badge */}
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <Star className="w-3 h-3 text-yellow-400 fill-current" />
                      <span className="text-yellow-400">4.9/5</span>
                      <span>from 2,500+ AI users</span>
                    </div>
                  </div>

                  <Button
                    onClick={() => handleLaunch('autonomous_ai')}
                    className="w-full h-12 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold text-lg"
                  >
                    Start My Autonomous AI ($97/mo)
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>

                  {/* Enhanced guarantees with prominent money-back */}
                  <div className="flex items-center justify-center gap-4 mt-4 text-xs text-gray-400">
                    <div className="flex items-center gap-1">
                      <Shield className="w-3 h-3 text-green-400" />
                      <span className="text-green-400 font-semibold">30-day money-back guarantee</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>7-day free trial</span>
                    </div>
                  </div>
                </motion.div>

                {/* Usage-Based Plan - Alternative Option */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl p-8 border border-gray-700/50"
                >
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">Pay-As-You-Go</h3>
                    <div className="text-3xl font-bold text-white mb-2">
                      $0
                      <span className="text-lg text-gray-400 font-normal">/month</span>
                    </div>
                    <div className="text-gray-400 text-sm mb-3">
                      Pay only for what you use
                    </div>
                    <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600/50">
                      <div className="text-blue-400 text-sm font-semibold">$12 per 10K emails</div>
                      <div className="text-gray-400 text-xs mt-1">No monthly commitment required</div>
                    </div>
                  </div>

                  <div className="space-y-3 mb-8">
                    {[
                      'Basic AI email writing & sending',
                      'Manual campaign setup required',
                      'Standard prospect database access',
                      'Email tracking & analytics',
                      'No setup fees or commitments',
                      'Upgrade to autonomous anytime'
                    ].map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-blue-400 flex-shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    variant="outline"
                    className="w-full h-12 border-gray-600 text-gray-300 hover:bg-gray-800/50 hover:text-white font-semibold"
                    onClick={() => handleLaunch('usage')}
                  >
                    Start with Usage-Based
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>

                  <div className="text-center mt-4 text-xs text-gray-400">
                    Perfect for testing and small campaigns
                  </div>
                </motion.div>
              </div>

              {/* Enterprise Link */}
              <div className="text-center mt-6 p-4 bg-gray-800/30 rounded-lg border border-gray-700/50 max-w-2xl mx-auto">
                <div className="text-gray-300 text-sm mb-2">
                  Need <strong>1M+ emails/month</strong> or custom AI training?
                </div>
                <button
                  onClick={() => window.open('https://cal.com/munger/enterprise', '_blank')}
                  className="text-blue-400 hover:text-blue-300 text-sm font-semibold underline"
                >
                  Talk to Enterprise Sales →
                </button>
              </div>

              {/* Enhanced Trust Indicators */}
              <div className="flex items-center justify-center gap-8 mt-8 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-400" />
                  <span>SOC2 Compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-blue-400" />
                  <span>Stripe Protected</span>  
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-400" />
                  <span>4.9/5 Customer Rating</span>
                </div>
              </div>

              {/* Social Proof */}
              <div className="text-center text-gray-400 text-sm max-w-2xl mx-auto">
                <p className="mb-2">
                  "Our autonomous AI marketer handles 90% of outbound marketing - from server setup to booking meetings"
                </p>
                <p>
                  Join 2,500+ businesses using AI automation to scale without traditional sales teams
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default ImprovedFlow; 